import Taxon from "src/models/taxon";
import type { CropMethod, RecommendedTaxon, TaxonCreatePayload, TaxonCycle, TaxonLite, TaxonPayload } from "src/types";
import Repository from "./repository";

const resource = "/taxa";

export default {
  async getAll() {
    const { data } = await Repository.get(`${resource}/`);
    // NB: Taxon tree structure does not able to sort them all by name in the API
    return data
      .sort((t1: TaxonPayload, t2: TaxonPayload) => (t1.name > t2.name ? 1 : -1))
      .map((taxon: TaxonPayload) => new Taxon(taxon));
  },
  async create(taxon: TaxonCreatePayload) {
    const { data } = await Repository.post(`${resource}/`, taxon);
    return new Taxon(data);
  },
  async getVarieties(taxonId: number) {
    const { data } = await Repository.get(`${resource}/${taxonId}/descendants/`, {
      params: { abstraction_level: "variety" },
    });
    // NB: Taxon tree structure does not able to sort them all by name in the API
    return data
      .sort((t1: TaxonPayload, t2: TaxonPayload) => (t1.name > t2.name ? 1 : -1))
      .map((taxon: TaxonPayload) => new Taxon(taxon));
  },
  async getCompanions(): Promise<RecommendedTaxon[]> {
    const { data } = await Repository.get(`${resource}/companions/`);
    return data;
  },
  async getUnusedSeeds(): Promise<TaxonLite[]> {
    const { data } = await Repository.get(`${resource}/unused-seeds/`);
    return data;
  },
  // Taxa that can be sown or planted in the current period.
  async getSowablePlantable(): Promise<TaxonLite[]> {
    const { data } = await Repository.get(`${resource}/sowable-plantable/`);
    return data;
  },
  async getClassification() {
    const { data } = await Repository.get(`${resource}/classification/`, {
      timeout: 15 * 1000,
    });
    return data;
  },
  // The growing methods available for a taxon (e.g. ["SEEDING_INHOUSE", "PLANT"]).
  async getMethods(taxonId: number): Promise<CropMethod[]> {
    const { data } = await Repository.get(`${resource}/${taxonId}/methods/`);
    return data;
  },
  // Cultivation cycles for a taxon, filtered by growing method(s). `methods`
  // serializes as repeated `methods=` params (methods=A&methods=B) to match the
  // API's list filter, not the bracketed form axios would produce by default.
  async getCycles(taxonId: number, methods: CropMethod[]): Promise<TaxonCycle[]> {
    const params = new URLSearchParams();
    methods.forEach((method) => params.append("methods", method));
    const query = params.toString();
    const url = query ? `${resource}/${taxonId}/cycles/?${query}` : `${resource}/${taxonId}/cycles/`;
    const { data } = await Repository.get(url);
    return data;
  },
};
